import { IoIosSearch, IoIosArrowRoundBack } from "react-icons/io";
import { Eye, Edit } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { IoCloseOutline } from "react-icons/io5"; // Ensure correct icon import
import { useNavigate } from "react-router-dom";
import { CiMenuBurger } from "react-icons/ci";
import { PiSortAscendingThin } from "react-icons/pi";
import axiosInstance from "../axiosinstance";

function ArchivedInvoice({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]); // State to hold invoice data
  const [filteredInvoices, setFilteredInvoices] = useState([]); // State to hold filtered invoice data
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [error, setError] = useState(null); // Error state to handle any issues during the fetch
  const [searchVisible, setSearchVisible] = useState(false); // Toggle search bar
  const [searchQuery, setSearchQuery] = useState(""); // Store search input
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isChecked, setChecked] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [isDeletePopup, setIsDeletePopup] = useState(false);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  const containerRef = useRef();
  // Fetch invoices from the API
  const fetchInvoices = async (status = "Archive") => {
    try {
      const response = await axiosInstance.get(
        `/api/get_invoices?page_status=${status}`
      );
      setInvoices(response.data.data); // Set invoices data from the response
      setFilteredInvoices(response.data.data); // Set filtered invoices to the same initially
      setLoading(false); // Set loading to false once data is fetched
    } catch (error) {
      setError("Failed to fetch invoices");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices("Archive");
  }, []); // Empty dependency array ensures this runs only once on component mount

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === "") {
      fetchInvoices();
    } else {
      try {
        setLoading(true);

        const response = await axiosInstance.get(`/api/search_invoice`, {
          params: {
            query,
            page_status: "Archive",
          },
        });

        setFilteredInvoices(response.data.result || []); // Update state with filtered data
        setLoading(false); // Stop loading
      } catch (error) {
        console.error("Error fetching search results:", error.message);
        setLoading(false); // Stop loading on error
      }
    }
  };

  // This function is called when clicking on the invoice number
  const Viewhistory = (invoice_id) => {
    navigate(`/Invoice-log/${invoice_id}`); // Navigate to the history page for the clicked invoice
  };

  function formatDate(dateString) {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
    };

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", options);
  }

  const handleEdit = (invoice_id) => {
    navigate(`/edit-invoice/${invoice_id}`);
  };

  const handleView = (id) => {
    navigate(`/View-Invoice/${id}`);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      const allInvoiceIds = invoices.map((invoice) => invoice.invoice_id);
      console.log("Selecting all invoices:", allInvoiceIds);
      setSelectedRows(allInvoiceIds);
    } else {
      console.log("Deselecting all invoices.");
      setSelectedRows([]);
    }

    setTimeout(() => setChecked(newSelectAll), 0);
  };
  const handleCheckboxChange = (invoiceId) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(invoiceId)
        ? prevSelected.filter((id) => id !== invoiceId)
        : [...prevSelected, invoiceId]
    );
  };
  useEffect(() => {
    const allSelected =
      invoices.length > 0 &&
      invoices.every((invoice) => selectedRows.includes(invoice.invoice_id));
    setSelectAll(allSelected);
  }, [selectedRows, invoices]);

  const handleCancel = () => {
    setIsDeletePopup(false);
  };

  const handleArchiveInvoice = async () => {
    console.log("📤 Archiving IDs:", selectedFirms);

    try {
      const response = await axiosInstance.put(`/api/archive_invoice`, {
        ids: selectedRows,
      });

      if (response.status === 200) {
        await fetchInvoices(); // Refresh list after archiving

        // Reset only after successful completion
        setSelectedFirms([]);
        setSelectAll(false);
        setIsDeletePopup(false); // Ensure popup closes
      }
    } catch (error) {
      console.error("❌ Error archiving invoices:", error);
      alert("Failed to archive invoices. Please try again.");
    }

    setIsDeletePopup(false);
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const handleArchivedTable = () => {
    navigate("/archived-invoice");
  };

  const handleRestoreUser = async () => {
    try {
      const response = await axiosInstance.put(`/api/unarchive_invoice`, {
        ids: selectedRows,
      });

      if (response.status === 200) {
        fetchInvoices();
        navigate("/invoice-management");
        setSelectAll(false);
        setChecked(false);
      } else {
        console.error("Failed to restore invoices");
      }
    } catch (error) {
      console.error("An error occurred while restoring departments:", error);
    }
  };
  const handleSort = (key) => {
    // If the same column is clicked again, toggle the direction.
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedInvoices = [...filteredInvoices].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Special handling for date fields
      if (key === "due_date" || key === "invoice_date") {
        aValue = new Date(a[key]);
        bValue = new Date(b[key]);
      }

      // Special handling for numerical fields (e.g., total_amount)
      if (key === "total_amount") {
        aValue = parseFloat(a[key]);
        bValue = parseFloat(b[key]);
      }

      // Compare the values and return the sort order.
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    // Update the sort configuration and set the sorted invoices
    setSortConfig({ key, direction });
    setFilteredInvoices(sortedInvoices); // Set sorted invoices
  };
  return (
    <div
      className={`table-container archived-invoice-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content archived-invoice-content">
        <div className="table-header">
          {selectedRows.length > 0 ? ( // Change from selectedFirms to selectedRows
            <div className="table-header-content archive-header">
              <button className="delete" onClick={handleRestoreUser}>
                Unarchive
              </button>
              <button
                className="action-header-close-button"
                onClick={() => setSelectedRows([])} // Deselect all checkboxes
              >
                <IoCloseOutline className="action-header-close-icon" />
              </button>
            </div>
          ) : (
            <div className="table-header-content">
              <div className="table-left-side">
                <div
                  onClick={() => navigate(-1)} // Navigate back to the previous page
                  className="back-button"
                >
                  <IoIosArrowRoundBack />
                </div>

                <h2 className="jost-bold">Archived Invoices</h2>
              </div>
              <div className="table-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search ..."
                    className="search-bar"
                    value={searchQuery}
                    onChange={handleSearch} // Use handleSearch to update search query and fetch data
                  />
                  <IoIosSearch className="search-bar-icon" />
                </div>
                <button
                  className="archived-invoice-sort-button"
                  onClick={handleSortpopup}
                >
                  <span className="action-icon">
                    <PiSortAscendingThin />
                  </span>
                </button>

                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-header">
                      <span className="sort-title">Sort By</span>
                    </div>
                    <div className="sort-popup-content">
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("client_name")}
                      >
                        Client Name
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("due_date")}
                      >
                        Due Date
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("total_amount")}
                      >
                        Total Amount
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("invoice_date")}
                      >
                        Date
                      </button>
                    </div>
                    <button
                      className="archive-button"
                      onClick={handleRestoreUser}
                    >
                      UnArchived Invoice
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="data-list-body archived-invoice-data-body">
          <div className="data-list-scroll-body archived-invoice-scroll-body">
            <div className="data-list-table">
              <table className="table multilevel-data archived-invoice-table">
                <thead className="sticky-header">
                  <tr className="table-row">
                    {/* <th>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th> */}
                    <th>Sr. No</th>
                    <th>Date</th>
                    <th>Invoice</th>
                    <th>Customer Name</th>
                    <th>Status</th>
                    <th>Due Date</th>
                    <th>Total Amount</th>
                    {/* <th>ACTION</th> */}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="center-align">
                        Loading invoices...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="9" className="center-align">
                        {error}
                      </td>
                    </tr>
                  ) : filteredInvoices?.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-data-text">
                        Currently, there are no records to display
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.invoice_id} className="hoverable-row">
                        {/* <td>
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(invoice.invoice_id)}
                            onChange={() =>
                              handleCheckboxChange(invoice.invoice_id)
                            }
                          />
                        </td> */}
                        <td className="center-align">
                          {invoice.serial_number}
                        </td>
                        <td className="center-align">
                          {formatDate(invoice.invoice_date)}
                        </td>
                        <td
                          className="center-align clickable"
                          onClick={() => Viewhistory(invoice.invoice_id)}
                        >
                          {invoice.invoice_number || "N/A"}
                        </td>
                        <td className="center-align">
                          {invoice.client_name || "N/A"}
                        </td>
                        <td>
                          <div
                            className={`invoice-status ${
                              invoice.status === "paid"
                                ? "status-paid"
                                : invoice.status === "unpaid"
                                ? "status-unpaid"
                                : "status-partial"
                            }`}
                          >
                            {invoice.status}
                          </div>
                        </td>
                        <td className="center-align">
                          {formatDate(invoice.due_date)}
                        </td>
                        <td className="center-align">
                          {invoice.total_amount ?? "N/A"}
                        </td>
                        {/* <td className="center-align">
                          <div className="action-btn">
                            <span
                              className="action-icon"
                              onClick={() => handleView(invoice.invoice_id)}
                            >
                              <Eye size={18} />
                            </span>
                            <span
                              className={`action-icon ${
                                invoice.status === "paid" ? "disabled" : ""
                              }`}
                              onClick={() =>
                                invoice.status !== "paid" &&
                                handleEdit(invoice.invoice_id)
                              }
                            >
                              <Edit size={18} />
                            </span>
                          </div>
                        </td> */}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArchivedInvoice;
